import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

function useSeats() {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/marketing/founders-club`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSeats(d))
      .catch(() => {});
  }, []);
  return seats;
}

const SeatCounter = ({ seats }) => (
  <div data-testid="founders-club-seat-counter" style={{
    display: 'inline-flex', alignItems: 'center', gap: 16,
    padding: '20px 28px',
    background: PALETTE.navy,
    borderLeft: `4px solid ${PALETTE.magenta}`,
  }}>
    <span style={{
      fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: seats && seats.closed ? PALETTE.gold : '#fff',
      background: seats && seats.closed ? 'transparent' : PALETTE.magenta,
      border: seats && seats.closed ? `1px solid ${PALETTE.gold}` : 'none',
      padding: '6px 12px', borderRadius: 3, flexShrink: 0,
    }} data-testid="founders-club-status">
      {seats && seats.closed ? 'Closed' : 'Open'}
    </span>
    <span style={{ fontSize: 15, color: '#E5E7EB', lineHeight: 1.45, maxWidth: 340 }}>
      {seats && seats.closed
        ? 'The 2026 Launch Partner cohort is closed. Standard licence is still available.'
        : <>The 2026 Launch Partner cohort is currently open across the EU and UK. It closes for good once the cohort is filled, <strong style={{ color: '#fff' }}>and this page will say so.</strong></>}
    </span>
  </div>
);

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [hp, setHp] = useState('');
  const [state, setState] = useState('idle');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === 'submitting') return;
    setState('submitting');
    try {
      const r = await fetch(`${API}/api/marketing/founders-club/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), website: hp, source_path: '/founders-club' }),
      });
      const d = await r.json();
      setState(d && d.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div data-testid="waitlist-success-message" style={{
        marginTop: 20, padding: '18px 22px', background: PALETTE.bgSoft,
        borderLeft: `3px solid ${PALETTE.gold}`, maxWidth: 560,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.navy }}>You’re on the list.</div>
        <div style={{ fontSize: 14, color: PALETTE.slate, marginTop: 4, lineHeight: 1.55 }}>
          If a place opens up, or a second cohort ever happens, you hear first. That&rsquo;s the only email this signup will ever generate.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="founders-club-waitlist-form" style={{ marginTop: 20, maxWidth: 560 }}>
      <div style={{ fontSize: 14.5, color: PALETTE.slate, lineHeight: 1.55, marginBottom: 14 }}>
        Leave your email and you&rsquo;ll be the first to hear if a place opens up or a second cohort happens. No newsletter, no drip sequence, one email at most.
      </div>
      <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} aria-hidden="true" name="website" />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)}
          data-testid="waitlist-name-input"
          style={{ flex: '1 1 160px', padding: '13px 14px', fontSize: 14.5, border: `1px solid ${PALETTE.border}`, borderRadius: 4, background: '#fff', color: PALETTE.ink }}
        />
        <input
          type="email" required placeholder="you@yourcompany.ie" value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="waitlist-email-input"
          style={{ flex: '2 1 220px', padding: '13px 14px', fontSize: 14.5, border: `1px solid ${PALETTE.border}`, borderRadius: 4, background: '#fff', color: PALETTE.ink }}
        />
        <button type="submit" data-testid="waitlist-submit-button" disabled={state === 'submitting'} style={{
          padding: '13px 22px', background: PALETTE.magenta, color: '#fff', border: 'none', borderRadius: 4,
          fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          cursor: state === 'submitting' ? 'wait' : 'pointer', opacity: state === 'submitting' ? 0.7 : 1,
        }}>
          {state === 'submitting' ? 'Joining\u2026' : 'Join the waitlist'}
        </button>
      </div>
      {state === 'error' && (
        <div data-testid="waitlist-error-message" style={{ fontSize: 13.5, color: '#991B1B', marginTop: 10 }}>
          That didn’t go through. Try again, or email hello@cloudvoro.com and we’ll add you by hand.
        </div>
      )}
    </form>
  );
};

export default function FoundersClubPage() {
  const seats = useSeats();

  const offerLd = null;

  const GET = [
    { t: 'Licence rate locked for 24 months', d: 'A locked recurring rate for the first two years, meaningfully below the standard quote for an operation of your size, agreed in writing before you sign. No mid-term increase, no per-user creep, no renegotiation letter in month 13.' },
    { t: 'Reduced fixed-scope implementation', d: 'A reduced fixed-scope implementation fee (typically half the standard quote) reflecting the operational access you give us during setup. Same discovery, same on-site days, same 30-day hand-holding, just a shared-value price.' },
    { t: 'Senior-led implementation, 12 months', d: 'The team that builds Keystone is the team on your floor and on your phone for the first twelve months. No account manager relay, no support-ticket queue.' },
    { t: 'Voice on the roadmap', d: 'A quarterly 30-minute product session with the people who set the roadmap. Launch Partner requests carry the same weight founding-customer requests always have, they ship first.' },
    { t: 'Data-out commitments in writing', d: 'CSV export of every core table on request. Optional read-only DB replica for the life of the contract. Full DB dump on exit within 48 hours. No exit fee, in writing.' },
  ];

  const GIVE = [
    { t: 'Company-name usage rights', d: 'Permission to reference your company by name and logo on the Keystone site and in one-to-one buyer conversations. You approve any specific claim before it goes public.' },
    { t: 'One written case study, within 6 months of go-live', d: 'A single before-and-after write-up (workflows replaced, trace times, audit prep, dispatch errors). We draft it, you edit and approve every word.' },
    { t: 'Reference conversations, one per quarter', d: 'A short introduction call between one prospective Keystone customer and your operations or QA lead each quarter. Never blindly forwarded; always scheduled through us.' },
    { t: 'Structured feedback, month 1 and month 3', d: 'One 45-minute review after the first month live, one after the third. What worked. What didn\u2019t. What would you build differently. Direct input into the roadmap.' },
    { t: 'Anonymised operational metrics', d: 'Permission to publish anonymised numbers (recall time, trace query time, audit prep hours) once you\u2019re comfortable they\u2019re real. Your company name never appears against a specific figure without your explicit sign-off.' },
  ];

  return (
    <SiteLayout>
      <SEO
        title="Keystone · 2026 Launch Partner Programme"
        description="A small, invitation-led cohort of EU and UK food manufacturers going live on Keystone during 2026. Commercial terms are agreed one-on-one after a written scope, not published on this page. Not a public discount."
        path="/founders-club"
        keywords="Keystone Launch Partner, 2026 food manufacturing software partner, Keystone reference customer"
        jsonLd={offerLd}
        noindex
      />

      {/* HERO */}
      <section data-testid="founders-club-hero" style={{ background: PALETTE.cream, padding: '160px 32px 90px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20 }}>
            2026 Launch Partner Programme · invitation-led
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(42px, 5.6vw, 72px)',
            fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0, maxWidth: 900,
          }}>
            A commercial partnership,<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>not a public discount.</em>
          </h1>
          <p style={{ fontSize: 20, lineHeight: 1.55, color: PALETTE.slate, marginTop: 28, maxWidth: 720 }}>
            The 2026 Launch Partner Programme is a small cohort of EU and UK food manufacturers going live on Keystone during 2026 in exchange for something more valuable than a coupon, name-usage rights, a written case study, and structured feedback that goes directly into the product. Commercial terms are agreed one-on-one after a written scope. We do not publish them on this page.
          </p>
          <div style={{ marginTop: 36 }}>
            <SeatCounter seats={seats} />
            {seats && seats.closed && <WaitlistForm />}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section style={{ background: PALETTE.paper, padding: '90px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>What a Launch Partner gets</div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            The same software.<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Commercial terms that reflect the risk you take with us.</em>
          </h2>
          <div style={{ marginTop: 44, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 20 }} data-testid="founders-club-benefits">
            {GET.map((b, i) => (
              <div key={b.t} style={{ padding: '26px 24px', background: PALETTE.bgSoft, borderTop: `2px solid ${PALETTE.magenta}` }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 13, color: PALETTE.magenta, fontWeight: 700 }}>0{i + 1}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: PALETTE.navy, margin: '10px 0 8px' }}>{b.t}</h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE ASK */}
      <section style={{ background: PALETTE.navy, padding: '90px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>What we ask in exchange</div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            Not money. <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Evidence.</em>
          </h2>
          <div style={{ marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }} data-testid="founders-club-exchange">
            {GIVE.map((g) => (
              <div key={g.t} style={{ padding: '24px', border: '1px solid rgba(255,255,255,0.14)' }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: PALETTE.gold, margin: '0 0 8px' }}>{g.t}</h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#E5E7EB', margin: 0 }}>{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE HONESTY NOTE */}
      <section style={{ background: PALETTE.cream, padding: '80px 32px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }} data-testid="founders-club-honesty-note">
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>Two things worth saying plainly</div>
          <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontStyle: 'italic', fontWeight: 500, color: PALETTE.navy, lineHeight: 1.45, margin: 0 }}>
            We deliberately do not publish a Launch Partner price, and we do not publish a standard one either. Countdown widgets and headline discounts are the oldest tricks in software sales and we&rsquo;re not playing them. Every quote, Launch Partner or standard, is sized to the operation and agreed one-on-one after a written scope.
          </p>
          <p style={{ fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 20 }}>
            First, the reduced licence rate ends after 24 months, then the standard rate applies, that&rsquo;s the deal in writing. Second, the separate Irish Founding-Customer Programme (the cohort that shaped the product before general availability, including Cashel Blue) is a different thing entirely; its brief lives at <Link to="/founding-customer" style={{ color: PALETTE.magenta, fontWeight: 600 }}>/founding-customer</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: PALETTE.paper, padding: '80px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 780, margin: '0 auto', textAlign: 'left' }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 40px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            {seats && seats.closed ? 'The 2026 cohort is closed. The software isn\u2019t.' : 'Ask about a Launch Partner conversation.'}
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, marginTop: 16, maxWidth: 620 }}>
            {seats && seats.closed
              ? 'Keystone is still available at the standard rate, with the same senior-led implementation. Tell us what you make and what you run on today.'
              : 'Tell us what you make and what you run on today. Twenty minutes with the team that builds it, a written scope within 48 hours if we fit, and a straight referral elsewhere if we don\u2019t. Launch Partner terms are discussed at scope stage, once we both know it&rsquo;s a fit.'}
          </p>
          <div style={{ display: 'flex', gap: 14, marginTop: 32, flexWrap: 'wrap' }}>
            <Link to="/site/contact" data-testid="founders-club-cta" style={{
              padding: '16px 28px', background: PALETTE.navy, color: '#fff', textDecoration: 'none',
              fontSize: 14, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 4,
            }}>
              Talk to the team →
            </Link>
            <Link to="/site/pricing" data-testid="founders-club-pricing-link" style={{
              padding: '16px 28px', color: PALETTE.navy, textDecoration: 'underline', textUnderlineOffset: 6,
              fontSize: 14, fontWeight: 600,
            }}>
              See how we price
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
